import { Chess } from "chess.js";
import { getLichessOpeningData, getOpeningBookMove } from "./getOpeningBookMove";
import { TimeControl, Level } from "./models";
import { sleep } from "./utils";
import _ from "lodash";

// Fetched 14. Dec 2024
export const LICHESS_DATA: Record<TimeControl, Record<Level, string>> = {
    ultraBullet: {
        "400": "1.3",
        "1000": "4.6",
        "1200": "12.2",
        "1400": "20.5",
        "1600": "19.5",
        "1800": "11.6",
        "2000": "4.5",
        "2200": "1.4",
        "2500": "0.0",
        Masters: "2.7",
    },
    bullet: {
        "400": "73.7",
        "1000": "157.1",
        "1200": "261.7",
        "1400": "366.6",
        "1600": "428.5",
        "1800": "398.2",
        "2000": "265.4",
        "2200": "147.5",
        "2500": "27.4",
        Masters: "2.7",
    },
    blitz: {
        "400": "124.3",
        "1000": "244.0",
        "1200": "395.9",
        "1400": "538.2",
        "1600": "610.0",
        "1800": "547.0",
        "2000": "318.2",
        "2200": "127.5",
        "2500": "10.6",
        Masters: "2.7",
    },
    rapid: {
        "400": "44.3",
        "1000": "88.4",
        "1200": "150.0",
        "1400": "202.5",
        "1600": "198.0",
        "1800": "137.4",
        "2000": "54.2",
        "2200": "11.9",
        "2500": "0.3",
        Masters: "2.7",
    },
    classical: {
        "400": "1.6",
        "1000": "6.9",
        "1200": "14.3",
        "1400": "21.3",
        "1600": "17.3",
        "1800": "8.7",
        "2000": "2.1",
        "2200": "0.2",
        "2500": "0.0",
        Masters: "2.7",
    },
    correspondence: {
        "400": "",
        "1000": "",
        "1200": "",
        "1400": "",
        "1600": "",
        "1800": "",
        "2000": "",
        "2200": "",
        "2500": "",
        Masters: "",
    },
};

export const generateLichessStatistics = async () => {
    const RATINGS = ["400", "1000", "1200", "1400", "1600", "1800", "2000", "2200", "2500", "Masters"];
    const TIME_CONTROLS = ["ultraBullet", "bullet", "blitz", "rapid", "classical"];
    const results = {
        ultraBullet: {
            "400": "",
            "1000": "",
            "1200": "",
            "1400": "",
            "1600": "",
            "1800": "",
            "2000": "",
            "2200": "",
            "2500": "",
            Masters: "",
        },
        bullet: {
            "400": "",
            "1000": "",
            "1200": "",
            "1400": "",
            "1600": "",
            "1800": "",
            "2000": "",
            "2200": "",
            "2500": "",
            Masters: "",
        },
        blitz: {
            "400": "",
            "1000": "",
            "1200": "",
            "1400": "",
            "1600": "",
            "1800": "",
            "2000": "",
            "2200": "",
            "2500": "",
            Masters: "",
        },
        rapid: {
            "400": "",
            "1000": "",
            "1200": "",
            "1400": "",
            "1600": "",
            "1800": "",
            "2000": "",
            "2200": "",
            "2500": "",
            Masters: "",
        },
        classical: {
            "400": "",
            "1000": "",
            "1200": "",
            "1400": "",
            "1600": "",
            "1800": "",
            "2000": "",
            "2200": "",
            "2500": "",
            Masters: "",
        },
        correspondence: {
            "400": "",
            "1000": "",
            "1200": "",
            "1400": "",
            "1600": "",
            "1800": "",
            "2000": "",
            "2200": "",
            "2500": "",
            Masters: "",
        },
    };

    for (const rating of RATINGS) {
        for (const timeControl of TIME_CONTROLS) {
            console.log("Fetching data for", rating, timeControl);
            const game = new Chess("rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1");
            const data = await getLichessOpeningData(game, rating, {
                timeControls: [timeControl],
            });
            await sleep(200);
            const total = data.white + data.black + data.draws;
            const totalInMillion = (total / 1000000).toFixed(1);
            results[timeControl][rating] = totalInMillion;
            console.log(`Rating: ${rating}, Time Control: ${timeControl}, Total: ${totalInMillion}M`);
        }
    }

    console.log(results);
};

export const checkLichessData = async () => {
    const data = {
        white: 254613472,
        draws: 21062747,
        black: 240040128,
        moves: [
            {
                uci: "e7e5",
                san: "e5",
                averageRating: 1691,
                white: 112583535,
                draws: 8925945,
                black: 98363260,
                game: null,
                opening: { eco: "C20", name: "King's Pawn Game" },
            },
            {
                uci: "c7c5",
                san: "c5",
                averageRating: 1710,
                white: 48542069,
                draws: 4081034,
                black: 49592858,
                game: null,
                opening: { eco: "B20", name: "Sicilian Defense" },
            },
            {
                uci: "e7e6",
                san: "e6",
                averageRating: 1704,
                white: 25200599,
                draws: 2202121,
                black: 25437920,
                game: null,
                opening: { eco: "C00", name: "French Defense" },
            },
            {
                uci: "d7d5",
                san: "d5",
                averageRating: 1700,
                white: 22078549,
                draws: 1933517,
                black: 21484602,
                game: null,
                opening: { eco: "B01", name: "Scandinavian Defense" },
            },
            {
                uci: "c7c6",
                san: "c6",
                averageRating: 1708,
                white: 16309529,
                draws: 1508849,
                black: 17020517,
                game: null,
                opening: { eco: "B10", name: "Caro-Kann Defense" },
            },
            {
                uci: "d7d6",
                san: "d6",
                averageRating: 1705,
                white: 7957929,
                draws: 661436,
                black: 7605352,
                game: null,
                opening: { eco: "B00", name: "Pirc Defense" },
            },
            {
                uci: "g7g6",
                san: "g6",
                averageRating: 1702,
                white: 7681896,
                draws: 651285,
                black: 7327983,
                game: null,
                opening: { eco: "B06", name: "Modern Defense" },
            },
            {
                uci: "b7b6",
                san: "b6",
                averageRating: 1696,
                white: 5602869,
                draws: 433795,
                black: 5109406,
                game: null,
                opening: { eco: "B00", name: "Owen Defense" },
            },
            {
                uci: "b8c6",
                san: "Nc6",
                averageRating: 1696,
                white: 3409461,
                draws: 260262,
                black: 3119434,
                game: null,
                opening: { eco: "B00", name: "Nimzowitsch Defense" },
            },
            {
                uci: "g8f6",
                san: "Nf6",
                averageRating: 1714,
                white: 2983481,
                draws: 250345,
                black: 3113567,
                game: null,
                opening: { eco: "B02", name: "Alekhine Defense" },
            },
            {
                uci: "a7a6",
                san: "a6",
                averageRating: 1691,
                white: 680289,
                draws: 50089,
                black: 599798,
                game: null,
                opening: { eco: "B00", name: "St. George Defense" },
            },
            {
                uci: "f7f5",
                san: "f5",
                averageRating: 1698,
                white: 488346,
                draws: 30719,
                black: 436594,
                game: null,
                opening: { eco: "B00", name: "Duras Gambit" },
            },
        ],
        recentGames: [
            {
                uci: "e7e5",
                id: "aLT5lcd4",
                winner: "white",
                speed: "blitz",
                mode: "rated",
                black: { name: "Gautama55", rating: 1756 },
                white: { name: "punosz", rating: 1799 },
                year: 2024,
                month: "2024-12",
            },
            {
                uci: "e7e5",
                id: "eIoy6BxX",
                winner: "white",
                speed: "rapid",
                mode: "rated",
                black: { name: "Erlantaskarin", rating: 1665 },
                white: { name: "sureshkvk", rating: 1721 },
                year: 2024,
                month: "2024-12",
            },
            {
                uci: "c7c6",
                id: "TOsFIcoX",
                winner: "black",
                speed: "blitz",
                mode: "rated",
                black: { name: "lmacdonald5288", rating: 1786 },
                white: { name: "mohame865", rating: 1792 },
                year: 2024,
                month: "2024-12",
            },
            {
                uci: "e7e5",
                id: "307TLyad",
                winner: "black",
                speed: "blitz",
                mode: "rated",
                black: { name: "mclovin6969", rating: 1715 },
                white: { name: "Illumininetys", rating: 1720 },
                year: 2024,
                month: "2024-12",
            },
            {
                uci: "d7d6",
                id: "iDUSENqJ",
                winner: "white",
                speed: "blitz",
                mode: "rated",
                black: { name: "Valemolto", rating: 1674 },
                white: { name: "the801cycles", rating: 1649 },
                year: 2024,
                month: "2024-12",
            },
            {
                uci: "c7c6",
                id: "0r07GS3u",
                winner: "white",
                speed: "rapid",
                mode: "rated",
                black: { name: "MarshalLJB", rating: 1726 },
                white: { name: "Sergey04041969", rating: 1643 },
                year: 2024,
                month: "2024-12",
            },
            {
                uci: "c7c5",
                id: "4YACJBxu",
                winner: "white",
                speed: "blitz",
                mode: "rated",
                black: { name: "TAlmazow69", rating: 1726 },
                white: { name: "Hayriatm", rating: 1730 },
                year: 2024,
                month: "2024-12",
            },
            {
                uci: "c7c5",
                id: "eJ48ENEm",
                winner: null,
                speed: "blitz",
                mode: "rated",
                black: { name: "SkidtMikkel", rating: 1717 },
                white: { name: "rodchrysler", rating: 1690 },
                year: 2024,
                month: "2024-12",
            },
        ],
        topGames: [],
        opening: { eco: "B00", name: "King's Pawn Game" },
    };
    const rejectionThreshold = 0;
    const playerColor = "w";
    const preferDubious = false;

    const results = [];

    for (let i = 0; i < 100000; i++) {
        const move = getOpeningBookMove(data as any, rejectionThreshold, playerColor, preferDubious);
        results.push(move?.san);
    }

    const frequency = _.countBy(results);
    console.log("frequency:", frequency);

    const frequencyInPercent = Object.entries(frequency).reduce((acc, [key, value]) => {
        acc[key] = ((value / results.length) * 100).toFixed(1);
        return acc;
    }, {});
    console.log("frequencyInPercent:", frequencyInPercent);

    const sortedFrequency = Object.entries(frequencyInPercent).sort((a, b) => parseFloat(b[1]) - parseFloat(a[1]));
    console.log("sortedFrequency:", sortedFrequency);
    for (const f of sortedFrequency) {
        console.log(f[0], Math.round(f[1]));
    }
};
